import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, esES, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { Button, Chip, LinearProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TorneoType } from '../../types/types';
import { useContext, useEffect, useState } from 'react';
import HeaderFab from '../HeaderFab';
import { UserContext } from '../../contexts/userContext';
import { useDeleteTorneo, useGetTorneos } from '../../hooks/useTorneos';
import TorneoDetails from './TorneoDetail';
import hash from 'object-hash';
import WizardTorneo from './WizardTorneo';
import { dateFormat } from '../Alumnos/AlumnosDetail';
import dayjs from 'dayjs';
import { grey } from '@mui/material/colors';

export function Torneos() {
  const { user } = useContext(UserContext);

  const torneos = useGetTorneos(user);
  const [verDetalleTorneo, setVerDetalleTorneo] = useState<TorneoType | undefined>();
  const [editarTorneo, setEditarTorneo] = useState<TorneoType | undefined | null>();

  const [torneoToDelete, setTorneoToDelete] = useState<string | null>(null);
  const deleteTorneo = useDeleteTorneo(torneoToDelete, user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!deleteTorneo.isLoading) {
      setTorneoToDelete(null);
      setTimeout(() => torneos.refetch(), 500);

    }
  }, [deleteTorneo.isLoading]);

  useEffect(() => {
    if (verDetalleTorneo) {
      const torneo = torneos.data?.find((t) => t._id === verDetalleTorneo._id);
      if (torneo) setVerDetalleTorneo(torneo);
    }
  }, [hash(torneos.data ?? {})]);

  if (!user?._id) return null;
  if (verDetalleTorneo) return (
    <TorneoDetails
      torneo={verDetalleTorneo}
      onClose={() => {setVerDetalleTorneo(undefined);}}
      refetchLista={() => torneos.refetch()}/>
  );

  const columns: GridColDef[] = [
    {
      field: 'torneo',
      headerName: 'Torneo',
      headerClassName: 'table-headers',
      width: 300,
      renderHeader: () => <strong>Torneo</strong>,
      valueGetter: (params) => params.row.nombre,
      renderCell: (params) => {
        const torneo = params.row as TorneoType;
        const torneoPasado = dayjs(params.row.fecha, dateFormat).isBefore(dayjs());
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left',
            height: '100%',
            color: torneoPasado ? grey[600] : undefined,
          }}>
            <Button
              onClick={() => {
                setVerDetalleTorneo(torneo);
              }}
              variant="text"
              color={torneoPasado ? undefined : 'success'}
              fullWidth
              style={{
                color: torneoPasado ? grey[600] : undefined,
                fontWeight: torneoPasado ? undefined : 'bold',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                paddingLeft: 0,
                justifyContent: 'flex-start',
                fontSize: torneoPasado ? 16 : 20,
              }}
            >
              {params.row.nombre}
            </Button>
            <Typography fontWeight="bold">{`${params.row.lugar} - ${params.row.organizador}`}</Typography>
            <Typography style={{ fontSize: 18 }} >{params.row.fecha}</Typography>
            <Typography >{params.row.tipo}</Typography>
            <Typography >{`Costo inscripcion: $${params.row.inscripcion}`}</Typography>
          </div>
        );
      },
    },
    {
      field: 'generos',
      flex: 1,
      headerName: 'Generos',
      headerClassName: 'table-headers',
      renderHeader: () => (<strong>Generos</strong>),
      renderCell: (params) => (
        <div style={{ textAlign: 'left' }}>
          {params.row.generos.map((p:string) => (
            <Chip
              key={p}
              label={p === 'F' ? 'Femenina' : 'Masculina'}
              size="small"
              style={{ margin: 4 }}
            />
          ))}
        </div>
      ),
    },
    {
      field: 'categorias',
      flex: 1,
      headerName: 'Categorias',
      headerClassName: 'table-headers',
      renderHeader: () => (<strong>Categorias</strong>),
      renderCell: (params) => (
        <div style={{ textAlign: 'left' }}>
          {params.row.categorias.map((p:string) => (
            <Chip
              key={p}
              label={p}
              size="small"
              style={{ margin: 4 }}
            />
          ))}
        </div>
      ),
    },
    {
      field: 'fecha',
      hide: true,
      valueGetter: (params) => dayjs(params.row.fecha, dateFormat).valueOf(),
    },
    {
      field: 'niveles',
      flex: 1,
      headerName: 'Niveles',
      headerClassName: 'table-headers',
      renderHeader: () => (<strong>Niveles</strong>),
      renderCell: (params) => (
        <div style={{ textAlign: 'left' }}>
          {params.row.niveles.map((p:string) => (
            <Chip
              key={p}
              label={p}
              size="small"
              style={{ margin: 4 }}
            />
          ))}
        </div>
      ),
    },
    {
      field: 'eliminar',
      headerName: 'Eliminar',
      headerClassName: 'table-headers',
      width: 100,
      hide: isMobile,
      renderHeader: () => '',
      renderCell: (params) => (
        <div style={{ display: 'block' }}>
          <Button
            onClick={() => setEditarTorneo(params.row)}
            variant='outlined'
            size='small'
            color="primary"
            style={{
              width: 85,
              marginTop: 16,
              whiteSpace: 'pre-wrap',
              textAlign: 'left',
            }}
          >
                Editar
          </Button>
          <br />
          <Button
            onClick={() => setTorneoToDelete(params.row._id)}
            variant='outlined'
            size='small'
            color="error"
            style={{
              width: 85,
              marginTop: 16,
              whiteSpace: 'pre-wrap',
              textAlign: 'left',
            }}
          >
                Eliminar
          </Button>
        </div>
      ),
    },
  ];

  if (torneos.isLoading || !torneos.data) return <LinearProgress />;


  return (
    <Box sx={{ height: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 115px)', width: 1 }}>
      <WizardTorneo
        torneo={editarTorneo ?? undefined}
        open={editarTorneo !== undefined}
        onClose={() => setEditarTorneo(undefined)}
        setOpen={() => setEditarTorneo(undefined)}
        refetchLista={() => torneos.refetch()}
      />
      {torneos.isFetching && <LinearProgress />}
      <DataGrid
        getRowHeight={() => 'auto'}
        rows={torneos.data}
        columns={columns}
        getRowId={(row) =>  row._id}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        components={{ Toolbar: GridToolbar }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'fecha', sort: 'desc' }],
          },
        }}
      />
      <HeaderFab
        title='Agregar Torneo'
        onclick={()=>{
          setEditarTorneo(null);
        }}
      />
    </Box>
  );
}
